import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  Skeleton,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
  Stack
} from "@mui/material";
import {
  ArrowForwardIos,
  ArrowBackIos,
  Star,
  StarBorder,
  Verified,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  PlayArrow,
  Close,
  Image,
  VideoCameraBack,
  BrokenImage
} from "@mui/icons-material";

// Mock token - replace with your actual token retrieval logic
const getAuthToken = () => {
  // This is the token from your curl command
  return 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJwcmF2ZWVuQGdyZXR0b20uY29tIiwiZXhwIjoxNzcxMjg2NTQ3LCJpYXQiOjE3Njc2ODY1NDcsImF1dGhvcml0aWVzIjoiIn0.sySdZ-LcE8lDjaOnlk8Rs-E3SzuUgAtzW5wElBkEd3dHqKYX9V1Ml31JdxGYDFYWKxOcqMDd2xVzOWvpnKmolw';
};

const ReviewSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollContainerRef = useRef(null);
  const mediaCarouselRefs = useRef({});
  const videoRefs = useRef({});

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState({});
  const [mediaLoading, setMediaLoading] = useState({});
  const [reviewMedia, setReviewMedia] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [authToken] = useState(getAuthToken()); // Get token once
  const [expandedReviews, setExpandedReviews] = useState({});

  // Modal states
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [mediaErrors, setMediaErrors] = useState({});
  const [mediaFetched, setMediaFetched] = useState({}); // Track which media has been fetched

  // Create data URL from base64
  const createDataUrl = (base64, mimeType) => {
    try {
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error('Error creating data URL:', error);
      return null;
    }
  };

  // Fetch media for a specific review - ONLY ONCE
  const fetchReviewMedia = useCallback(async (ratingId) => {
    if (!ratingId || !authToken || mediaFetched[ratingId]) {
      return; // Don't fetch if already fetched or no token
    }

    // Mark as being fetched
    setMediaFetched(prev => ({ ...prev, [ratingId]: true }));
    setMediaLoading(prev => ({ ...prev, [ratingId]: true }));

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/review-rating/review-rating/media/${ratingId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            // 'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && result.data.allMedia) {
        const mediaWithUrls = result.data.allMedia.map((media, index) => {
          let mediaUrl = null;

          if (media.base64Content) {
            // Determine MIME type
            let mimeType = '';
            if (media.mediaType === 'VIDEO') {
              mimeType = 'video/mp4';
            } else {
              mimeType = getImageMimeType(media.fileName);
            }

            // Create data URL directly
            mediaUrl = createDataUrl(media.base64Content, mimeType);
          }

          return {
            ...media,
            mediaUrl,
            thumbnailUrl: mediaUrl, // Use same URL for thumbnail
            index,
            mimeType: media.mediaType === 'VIDEO' ? 'video/mp4' : getImageMimeType(media.fileName)
          };
        });

        setReviewMedia(prev => ({
          ...prev,
          [ratingId]: {
            media: mediaWithUrls,
            totalPhotos: result.data.totalPhotos || 0,
            totalVideos: result.data.totalVideos || 0,
            totalMediaFiles: result.data.totalMediaFiles || 0
          }
        }));

        // Clear any errors for this review
        Object.keys(mediaErrors).forEach(key => {
          if (key.startsWith(`${ratingId}-`)) {
            setMediaErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[key];
              return newErrors;
            });
          }
        });

      } else {
        // No media available
        setReviewMedia(prev => ({
          ...prev,
          [ratingId]: {
            media: [],
            totalPhotos: 0,
            totalVideos: 0,
            totalMediaFiles: 0
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching media for review ${ratingId}:`, error);

      // Set error for this review
      setMediaErrors(prev => ({
        ...prev,
        [`${ratingId}-fetch-error`]: true
      }));

      // Even on error, mark as fetched so we don't retry
      setReviewMedia(prev => ({
        ...prev,
        [ratingId]: {
          media: [],
          totalPhotos: 0,
          totalVideos: 0,
          totalMediaFiles: 0,
          error: true
        }
      }));
    } finally {
      setMediaLoading(prev => ({ ...prev, [ratingId]: false }));
    }
  }, [authToken, mediaFetched, mediaErrors]);

  // Helper function to get MIME type from filename
  const getImageMimeType = (filename) => {
    if (!filename) return 'image/jpeg';

    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'bmp':
        return 'image/bmp';
      default:
        return 'image/jpeg';
    }
  };

  // Fetch reviews from API
  const fetchReviews = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      // Admin listed reviews API - sirf wahi dikhao jo admin ne feature kiye hain
      const response = await fetch(
        `https://car.amar.co.jp/api/review-rating/approved-review/get-list?page=${page}&size=50`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && Array.isArray(result.data.reviews)) {
        const reviewsWithImages = await Promise.all(
          result.data.reviews.map(async (review) => {
            let carImageUrl = null;

            if (review.carModelId) {
              try {
                const imageResponse = await fetch(
                  `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${review.carModelId}`,
                  {
                    method: 'GET',
                    headers: {
                      'accept': '*/*'
                    }
                  }
                );

                if (imageResponse.ok && imageResponse.headers.get('content-type')?.includes('image')) {
                  const imageBlob = await imageResponse.blob();
                  carImageUrl = URL.createObjectURL(imageBlob);
                }
              } catch (error) {
                console.error(`Error fetching car image for ID ${review.carModelId}:`, error);
              }
            }

            return {
              id: review.ratingId,
              orderId: review.orderId,
              userId: review.userId,
              carModelId: review.carModelId,
              rating: review.rating || 0,
              comment: review.shareYourExperince || "No comment provided",
              verified: review.verifiedPurchase || false,
              ratingBadge: review.ratingBadge || "",
              isAdminApproveRating: review.isAdminApproveRating || false,
              reviewStatus: review.reviewStatus || "APPROVED",
              firstName: review.firstName || "",
              lastName: review.lastName || "",
              profilePhoto: review.profilePhoto,
              userCountry: review.userCountry || "Unknown",
              carBrandName: review.carBrandName || "Unknown Brand",
              carModelName: review.carModelName || "Unknown Model",
              carCategoriesName: review.carCategoriesName || "",
              userEmail: review.userEmail || "",
              userMobileNo: review.userMobileNo || "",
              carImage: carImageUrl,
              date: review.createdAt ? formatDateFromArray(review.createdAt) : new Date().toISOString().split('T')[0]
            };
          })
        );

        setReviews(reviewsWithImages);
        setTotalPages(result.data.totalPages || 1);
        setCurrentPage(result.data.currentPage || 0);

        // Load media for all reviews immediately
        if (authToken) {
          reviewsWithImages.forEach(review => {
            fetchReviewMedia(review.id);
          });
        }

      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [fetchReviewMedia, authToken]);

  // Format date from API array format
  const formatDateFromArray = (dateArray) => {
    if (!dateArray || dateArray.length < 3) return new Date().toISOString().split('T')[0];

    const [year, month, day] = dateArray;
    const date = new Date(year, month - 1, day);
    return date.toISOString().split('T')[0];
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Unknown date";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      return dateString;
    }
  };

  // Get user initials
  const getInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName[0] : 'U';
    const lastInitial = lastName ? lastName[0] : 'S';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  // Get avatar color based on user ID
  const getAvatarColor = (userId) => {
    const colors = [
      "#eb2329", "#2c3e50", "#009966", "#6c2aab",
      "#FF5722", "#2196F3", "#4CAF50", "#9C27B0",
      "#FF9800", "#795548", "#607D8B", "#3F51B5"
    ];
    return colors[userId % colors.length] || "#eb2329";
  };

  // Get rating color
  const getRatingColor = (rating) => {
    if (rating >= 4.5) return "#e9b417ff";
    if (rating >= 4) return "#e9b417ff";
    if (rating >= 3) return "#FFC107";
    if (rating >= 2) return "#FF9800";
    return "#F44336";
  };

  // Scroll functions for main carousel
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = isMobile ? 280 : isTablet ? 320 : 380;
      scrollContainerRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = isMobile ? 280 : isTablet ? 320 : 380;
      scrollContainerRef.current.scrollBy({
        left: cardWidth,
        behavior: 'smooth'
      });
    }
  };

  // Media carousel navigation
  const scrollMediaLeft = (reviewId) => {
    const container = mediaCarouselRefs.current[reviewId];
    if (container) {
      const mediaWidth = isMobile ? 85 : isTablet ? 95 : 100;
      container.scrollBy({ left: -mediaWidth, behavior: 'smooth' });
    }
  };

  const scrollMediaRight = (reviewId) => {
    const container = mediaCarouselRefs.current[reviewId];
    if (container) {
      const mediaWidth = isMobile ? 85 : isTablet ? 95 : 100;
      container.scrollBy({ left: mediaWidth, behavior: 'smooth' });
    }
  };

  // Next/previous media in modal
  const nextMedia = () => {
    const currentReviewMedia = reviewMedia[selectedReviewId]?.media;
    if (currentReviewMedia) {
      setSelectedMediaIndex(prev =>
        prev < currentReviewMedia.length - 1 ? prev + 1 : 0
      );
    }
  };

  const previousMedia = () => {
    const currentReviewMedia = reviewMedia[selectedReviewId]?.media;
    if (currentReviewMedia) {
      setSelectedMediaIndex(prev =>
        prev > 0 ? prev - 1 : currentReviewMedia.length - 1
      );
    }
  };

  // Open media modal
  const openMediaModal = (reviewId, mediaIndex = 0) => {
    setSelectedReviewId(reviewId);
    setSelectedMediaIndex(mediaIndex);
    setMediaModalOpen(true);
  };

  // Close media modal
  const closeMediaModal = () => {
    setMediaModalOpen(false);
    setSelectedReviewId(null);
    setSelectedMediaIndex(0);

    // Pause any playing videos
    Object.values(videoRefs.current).forEach(video => {
      if (video && !video.paused) {
        video.pause();
      }
    });
  };

  // Load next page of reviews
  const loadNextPage = () => {
    if (currentPage < totalPages - 1) {
      fetchReviews(currentPage + 1);
    }
  };

  // Load previous page of reviews
  const loadPreviousPage = () => {
    if (currentPage > 0) {
      fetchReviews(currentPage - 1);
    }
  };

  // Handle image load
  const handleImageLoad = (reviewId) => {
    setImageLoading(prev => ({ ...prev, [reviewId]: false }));
  };

  const toggleReviewExpand = (reviewId) => {
  setExpandedReviews(prev => ({
    ...prev,
    [reviewId]: !prev[reviewId]
  }));
};

  // Handle image error
  const handleImageError = (reviewId) => {
    setImageLoading(prev => ({ ...prev, [reviewId]: false }));
  };

  // Handle media load error
  const handleMediaError = (reviewId, mediaIndex, type = 'media') => {
    setMediaErrors(prev => ({
      ...prev,
      [`${reviewId}-${mediaIndex}-${type}`]: true
    }));
  };

  // Initial fetch of reviews
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup car images
      reviews.forEach(review => {
        if (review.carImage && review.carImage.startsWith('blob:')) {
          URL.revokeObjectURL(review.carImage);
        }
      });

      // Cleanup video refs
      Object.keys(videoRefs.current).forEach(key => {
        delete videoRefs.current[key];
      });
    };
  }, [reviews]);

  // Render media thumbnail
  const renderMediaThumbnail = (media, reviewId, index) => {
    const errorKey = `${reviewId}-${index}-thumbnail`;
    const hasError = mediaErrors[errorKey];
    const isLoading = mediaLoading[reviewId];

    const handleClick = () => {
      if (!isLoading) {
        openMediaModal(reviewId, index);
      }
    };

    const handleThumbnailError = () => {
      handleMediaError(reviewId, index, 'thumbnail');
    };

    if (media.mediaType === 'VIDEO') {
      return (
        <Box
          key={media.mediaId}
          onClick={handleClick}
          sx={{
            position: 'relative',
            minWidth: { xs: 85, sm: 90, md: 100 },
            width: { xs: 85, sm: 90, md: 100 },
            height: { xs: 60, sm: 63, md: 70 },
            borderRadius: { xs: 0.75, sm: 1 },
            overflow: 'hidden',
            backgroundColor: '#333',
            cursor: 'pointer',
            flexShrink: 0,
            touchAction: 'manipulation',
            transition: 'all 0.2s ease',
            '&:hover': {
              '& .play-icon': {
                transform: 'scale(1.1)',
              },
              '&::after': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              }
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              transition: 'background-color 0.2s',
            }
          }}
        >
          {hasError ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                backgroundColor: '#f5f5f5',
                gap: { xs: 0.25, sm: 0.5 }
              }}
            >
              <BrokenImage sx={{
                color: '#ccc',
                fontSize: { xs: 18, sm: 20, md: 24 },
                mb: { xs: 0.25, sm: 0.5 }
              }} />
              <Typography variant="caption" color="text.secondary" sx={{
                fontSize: { xs: '0.625rem', sm: '0.688rem', md: '0.75rem' }
              }}>
                Video
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Video thumbnail with play icon */}
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {media.thumbnailUrl ? (
                    <>
                      <img
                        src={media.thumbnailUrl}
                        alt={`Video thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          opacity: 0.7
                        }}
                        onError={handleThumbnailError}
                      />
                      <PlayArrow
                        className="play-icon"
                        sx={{
                          color: 'white',
                          fontSize: { xs: 24, sm: 28, md: 32 },
                          transition: 'transform 0.2s',
                          position: 'absolute',
                          zIndex: 2,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                        }}
                      />
                    </>
                  ) : (
                    <PlayArrow
                      className="play-icon"
                      sx={{
                        color: 'white',
                        fontSize: { xs: 24, sm: 28, md: 32 },
                        transition: 'transform 0.2s',
                        position: 'absolute',
                        zIndex: 2
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Chip
                label="Video"
                size="small"
                sx={{
                  position: 'absolute',
                  top: { xs: 3, sm: 3.5, md: 4 },
                  right: { xs: 3, sm: 3.5, md: 4 },
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  fontSize: { xs: '0.563rem', sm: '0.594rem', md: '0.6rem' },
                  height: { xs: 18, sm: 19, md: 20 },
                  zIndex: 2,
                  '& .MuiChip-label': {
                    px: { xs: 0.75, sm: 1 }
                  }
                }}
              />
            </>
          )}
        </Box>
      );
    }

    // Image thumbnail
    return (
      <Box
        key={media.mediaId}
        onClick={handleClick}
        sx={{
          position: 'relative',
          minWidth: { xs: 85, sm: 90, md: 100 },
          width: { xs: 85, sm: 90, md: 100 },
          height: { xs: 60, sm: 63, md: 70 },
          borderRadius: { xs: 0.75, sm: 1 },
          overflow: 'hidden',
          backgroundColor: '#f0f0f0',
          cursor: 'pointer',
          flexShrink: 0,
          touchAction: 'manipulation',
          transition: 'all 0.2s ease',
          '&:hover': {
            '&::after': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s',
          }
        }}
      >
        {hasError ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              backgroundColor: '#f5f5f5',
              gap: { xs: 0.25, sm: 0.5 }
            }}
          >
            <BrokenImage sx={{
              color: '#ccc',
              fontSize: { xs: 18, sm: 20, md: 24 },
              mb: { xs: 0.25, sm: 0.5 }
            }} />
            <Typography variant="caption" color="text.secondary" sx={{
              fontSize: { xs: '0.625rem', sm: '0.688rem', md: '0.75rem' }
            }}>
              Image
            </Typography>
          </Box>
        ) : media.mediaUrl ? (
          <img
            src={media.mediaUrl}
            alt={`Review media ${index + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={handleThumbnailError}
            loading="lazy"
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}
          >
            <Image sx={{ color: '#ccc', fontSize: { xs: 20, sm: 22, md: 24 } }} />
          </Box>
        )}
      </Box>
    );
  };

  // Render media content in modal
  const renderModalMedia = (media, reviewId, index) => {
    const errorKey = `${reviewId}-${index}-modal`;
    const hasError = mediaErrors[errorKey];

    const handleMediaError = () => {
      setMediaErrors(prev => ({
        ...prev,
        [errorKey]: true
      }));
    };

    if (media.mediaType === 'VIDEO') {
      return (
        <Box
          key={media.mediaId}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          {hasError ? (
            <Box
              sx={{
                textAlign: 'center',
                p: 3
              }}
            >
              <BrokenImage sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Unable to load video
              </Typography>
            </Box>
          ) : media.mediaUrl ? (
            <video
              ref={el => videoRefs.current[`${reviewId}-${index}`] = el}
              src={media.mediaUrl}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              onError={handleMediaError}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                p: 3
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Video not available
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Image in modal
    return (
      <Box
        key={media.mediaId}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {hasError ? (
          <Box
            sx={{
              textAlign: 'center',
              p: 3
            }}
          >
            <BrokenImage sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Unable to load image
            </Typography>
          </Box>
        ) : media.mediaUrl ? (
          <img
            src={media.mediaUrl}
            alt={`Review media ${index + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
            onError={handleMediaError}
            loading="lazy"
          />
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              p: 3
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Image not available
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render media carousel for a review
  const renderMediaCarousel = (review) => {
    const mediaData = reviewMedia[review.id];
    const hasMedia = mediaData?.media?.length > 0;
    const isLoading = mediaLoading[review.id];
    const hasError = mediaErrors[`${review.id}-fetch-error`];
    const wasFetched = mediaFetched[review.id];

    // Show loading only if we haven't fetched yet and are currently loading
    if (isLoading && !wasFetched) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{
            mb: 1,
            display: 'block',
            fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
          }}>
            Loading media...
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={{ xs: 85, sm: 90, md: 100 }}
                height={{ xs: 60, sm: 63, md: 70 }}
                sx={{ borderRadius: { xs: 0.75, sm: 1 } }}
              />
            ))}
          </Box>
        </Box>
      );
    }

    if (hasError) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="error" sx={{
            display: 'block',
            fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
          }}>
            Failed to load media
          </Typography>
        </Box>
      );
    }

    if (!hasMedia) {
      // If we've fetched and there's no media, show nothing
      if (wasFetched) {
        return null;
      }
      // If not fetched yet but we have token, it will load automatically
      return null;
    }

    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{
          mb: 1,
          display: 'block',
          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' }
        }}>
          {mediaData.totalPhotos > 0 && `${mediaData.totalPhotos} photo${mediaData.totalPhotos !== 1 ? 's' : ''}`}
          {mediaData.totalPhotos > 0 && mediaData.totalVideos > 0 && ' • '}
          {mediaData.totalVideos > 0 && `${mediaData.totalVideos} video${mediaData.totalVideos !== 1 ? 's' : ''}`}
        </Typography>

        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Media carousel container */}
          <Box
            ref={(el) => mediaCarouselRefs.current[review.id] = el}
            sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 0.75 },
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              py: 0.5,
              width: '100%',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {mediaData.media.map((media, index) =>
              renderMediaThumbnail(media, review.id, index)
            )}
          </Box>

          {/* Media carousel navigation arrows */}
          {mediaData.media.length > 3 && (
            <>
              <IconButton
                onClick={() => scrollMediaLeft(review.id)}
                sx={{
                  position: 'absolute',
                  left: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  },
                  width: { xs: 24, sm: 28 },
                  height: { xs: 24, sm: 28 },
                  minWidth: { xs: 24, sm: 28 },
                  zIndex: 1,
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                <ArrowBackIos sx={{ fontSize: { xs: 12, sm: 14 }, ml: 0.5 }} />
              </IconButton>
              <IconButton
                onClick={() => scrollMediaRight(review.id)}
                sx={{
                  position: 'absolute',
                  right: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  },
                  width: { xs: 24, sm: 28 },
                  height: { xs: 24, sm: 28 },
                  minWidth: { xs: 24, sm: 28 },
                  zIndex: 1,
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                <ArrowForwardIos sx={{ fontSize: { xs: 12, sm: 14 } }} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    );
  };

  // Compact media renderer for card
  const renderCompactMedia = (review) => {
    const mediaData = reviewMedia[review.id];
    const hasMedia = mediaData?.media?.length > 0;

    if (!hasMedia) return null;

    const handleMediaClick = (index) => {
      openMediaModal(review.id, index);
    };

    return (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{
          display: "block",
          fontSize: "0.75rem",
          mb: 0.5,
        }}>
          {mediaData.totalPhotos > 0 && `${mediaData.totalPhotos} photo${mediaData.totalPhotos !== 1 ? 's' : ''}`}
          {mediaData.totalPhotos > 0 && mediaData.totalVideos > 0 && ' • '}
          {mediaData.totalVideos > 0 && `${mediaData.totalVideos} video${mediaData.totalVideos !== 1 ? 's' : ''}`}
        </Typography>

        <Box sx={{ display: "flex", gap: 0.5, overflowX: "auto" }}>
          {mediaData.media.slice(0, 3).map((media, index) => (
            media.mediaType === 'VIDEO' ? (
              <Box
                key={media.mediaId}
                onClick={() => handleMediaClick(index)}
                sx={{
                  width: 50,
                  height: 35,
                  borderRadius: 1,
                  backgroundColor: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  }
                }}
              >
                {media.thumbnailUrl ? (
                  <>
                    <img
                      src={media.thumbnailUrl}
                      alt={`Video thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.7
                      }}
                    />
                    <PlayArrow sx={{
                      color: 'white',
                      fontSize: 16,
                      position: 'absolute',
                      zIndex: 2
                    }} />
                  </>
                ) : (
                  <PlayArrow sx={{
                    color: 'white',
                    fontSize: 16,
                    position: 'absolute',
                    zIndex: 2
                  }} />
                )}
              </Box>
            ) : media.mediaUrl ? (
              <Box
                key={media.mediaId}
                onClick={() => handleMediaClick(index)}
                sx={{
                  width: 50,
                  height: 35,
                  borderRadius: 1,
                  overflow: 'hidden',
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  }
                }}
              >
                <img
                  src={media.mediaUrl}
                  alt={`Review media ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ) : null
          ))}
          {mediaData.media.length > 3 && (
            <Box
              onClick={() => handleMediaClick(0)}
              sx={{
                width: 50,
                height: 35,
                borderRadius: 1,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Typography variant="caption" color="text.secondary">
                +{mediaData.media.length - 3}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", py: 10, position: "relative", backgroundColor: "#f8f9fa" }}>

      {/* Header Section with Updated Styling */}
      <Box sx={{ textAlign: "center", mb: 4, position: 'relative' }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "2rem", md: "2.5rem" },
            background: "linear-gradient(135deg, #eb2329 0%, #ff6b6b 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 2,
          }}
        >
          Customer Reviews
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            maxWidth: "600px",
            mx: "auto",
            mb: 3,
            fontSize: { xs: "0.9rem", md: "1.1rem" },
            paddingInline: "5px",
          }}
        >
          See what our customers say about their car buying experience
        </Typography>

        {/* Overall Rating Summary */}
        {!loading && reviews.length > 0 && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexWrap: 'wrap',
          }}>
            <Rating
              value={reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length}
              readOnly
              precision={0.1}
              size="large"
              sx={{ mb: 0 }}
              icon={<StarIcon sx={{ color: "#ffb400", fontSize: { xs: 22, md: 26 } }} />}
              emptyIcon={<StarBorderIcon sx={{ color: "#ddd", fontSize: { xs: 22, md: 26 } }} />}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", fontSize: { xs: "0.938rem", md: "1.063rem" } }}>
              {reviews.length} Verified Reviews
            </Typography>
          </Box>
        )}
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "350px",
          gap: 2.5
        }}>
          <CircularProgress size={50} sx={{ color: "#eb2329" }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: "0.938rem", md: "1.063rem" } }}>
            Loading reviews...
          </Typography>
        </Box>
      ) : reviews.length === 0 ? (
        <Box sx={{
          textAlign: "center",
          py: 6,
          minHeight: "350px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: "1.063rem", md: "1.25rem" } }}>
            No reviews found
          </Typography>
          <Typography color="text.disabled" sx={{ fontSize: { xs: "0.875rem", md: "0.938rem" } }}>
            Check back later for customer reviews
          </Typography>
        </Box>
      ) : (
        <>
          {/* Navigation Arrows */}
          {!isMobile && (
            <>
              <IconButton
                onClick={scrollLeft}
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  zIndex: 10,
                  width: 40,
                  height: 40,
                }}
              >
                <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
              </IconButton>
              <IconButton
                onClick={scrollRight}
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  zIndex: 10,
                  width: 40,
                  height: 40,
                }}
              >
                <ArrowForwardIos sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}

          {/* Reviews Carousel with Updated Card Sizes */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              scrollBehavior: "smooth",
              px: { xs: 2, md: 6 },
              py: 2,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {reviews.map((review) => (
              <Card
                key={review.id}
                sx={{
                  // Changed from fixed minWidth/maxWidth to responsive values
                  minWidth: { xs: "calc(100vw - 64px)", sm: 280, md: 300 },
                  maxWidth: { xs: "calc(100vw - 64px)", sm: 280, md: 300 },
                  flex: "0 0 auto",
                  border: "none",
                  borderRadius: 3,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: "linear-gradient(145deg, #f5f5f5 0%, #f8f9fa 100%)",
                  "&:hover": {
                    transform: { xs: "none", sm: "translateY(-8px) scale(1.01)" },
                    boxShadow: { xs: "0 8px 30px rgba(0,0,0,0.08)", sm: "0 15px 40px rgba(235, 35, 41, 0.12)" },
                  },
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  // Prevent card from shrinking too much on mobile
                  flexShrink: 0,
                  // Add these to handle safe areas on iPhone
                  "@supports (padding: max(0px))": {
                    marginLeft: "max(16px, env(safe-area-inset-left))",
                    marginRight: "max(16px, env(safe-area-inset-right))",
                  },
                }}
              >
                {/* Car Image Section - Fixed Height */}
              {/* Car Image Section - Fixed Height */}
<Box sx={{
  position: "relative",
  height: "200px",
  width: "100%",
  overflow: "hidden",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  background: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}}>
  {review.carImage ? (
    <>
      {imageLoading[review.id] !== false && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      )}
      <img
        src={review.carImage}
        alt={`${review.carBrandName} ${review.carModelName}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          opacity: imageLoading[review.id] === false ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
        onLoad={() => handleImageLoad(review.id)}
        onError={() => handleImageError(review.id)}
      />
    </>
  ) : (
    <Box sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f5f5f5",
    }}>
      <img
        src="/carousel/carslider.png"
        alt="Car placeholder"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    </Box>
  )}

  {/* Rating Badge positioned like Code 2 */}
  {review.ratingBadge && (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        backgroundColor: review.ratingBadge === "TOP_RATED" ? "#05f287ff" : review.ratingBadge === "MEDIUM_RATED" ? "#fff3cd" : "#fee2e2",
        color: review.ratingBadge === "TOP_RATED" ? "rgba(10, 11, 11, 1)" : review.ratingBadge === "MEDIUM_RATED" ? "#856404" : "#991b1b",
        px: 1.5,
        py: 0.3,
        borderRadius: "12px",
        fontSize: "0.7rem",
        fontWeight: 600,
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        border: review.ratingBadge === "TOP_RATED" ? "1px solid #05f287ff" : review.ratingBadge === "MEDIUM_RATED" ? "1px solid #856404" : "1px solid #991b1b",
      }}
    >
      {review.ratingBadge === "TOP_RATED" ? "TOP RATED" : review.ratingBadge === "MEDIUM_RATED" ? "MEDIUM RATED" : "LOW RATED"}
    </Box>
  )}
</Box>

                <CardContent sx={{
                  p: 2,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100% - 200px)", // Fixed height for content

                }}>
                  {/* Car Brand and Model */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        lineHeight: 1.3,
                        mb: 0.5,
                        background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {review.carBrandName} {review.carModelName}
                    </Typography>
                    {review.carCategoriesName && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.85rem",
                          mb: 1,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {review.carCategoriesName}
                      </Typography>
                    )}
                  </Box>

                  {/* Rating */}
                  <Box sx={{ mb: 1.5 }}>
                    <Rating
                      value={review.rating}
                      readOnly
                      precision={0.5}
                      size="small"
                      sx={{ mb: 0.5 }}
                      icon={<Star sx={{
                        color: getRatingColor(review.rating),
                        fontSize: 18
                      }} />}
                      emptyIcon={<StarBorder sx={{
                        color: "#ddd",
                        fontSize: 18
                      }} />}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                      {review.rating.toFixed(1)} / 5.0
                    </Typography>
                  </Box>

                  {/* Review Comment - Fixed Word Breaking */}
               {/* Review Comment - Fixed Word Breaking */}
<Box
  sx={{
    position: "relative",
    pl: 1.25,
    mb: 1.5,
    width: "100%",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "2.5px",
      backgroundColor: "#eb2329",
      borderRadius: "1.25px",
      zIndex: 1,
    },
  }}
>
  <Box
    sx={{
      overflow: "hidden",
      position: "relative",
      paddingLeft: "8px",
      marginLeft: "-8px",
      width: "100%",
      maxWidth: "100%",
      maxHeight: expandedReviews[review.id] ? "none" : "80px",
      transition: "max-height 0.3s ease",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        lineHeight: 1.5,
        fontSize: { xs: "0.8rem", sm: "0.85rem" },
        fontStyle: "italic",
        display: "block",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        wordWrap: "break-word",
        hyphens: "auto",
        whiteSpace: "normal",
        "@media (max-width: 900px)": {
          overflowWrap: "anywhere",
          maxWidth: "calc(100vw - 80px)",
        },
        "@media (max-width: 400px)": {
          fontSize: "0.75rem",
          maxWidth: "calc(100vw - 60px)",
        },
      }}
    >
      {review.comment}
    </Typography>

    {/* Fade effect at bottom - only when collapsed */}
    {!expandedReviews[review.id] && review.comment && review.comment.length > 100 && (
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "20px",
          background: "linear-gradient(to bottom, transparent, #f8f9fa)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />
    )}
  </Box>

  {/* Read more / Read less toggle */}
  {review.comment && review.comment.length > 100 && (
    <Typography
      onClick={() => toggleReviewExpand(review.id)}
      sx={{
        color: "#eb2329",
        fontSize: "0.75rem",
        fontWeight: 600,
        cursor: "pointer",
        mt: 0.5,
        pl: "8px",
        display: "inline-block",
        userSelect: "none",
        "&:hover": {
          textDecoration: "underline",
        },
      }}
    >
      {expandedReviews[review.id] ? "Read less" : "Read more"}
    </Typography>
  )}
</Box>

                  {/* User Info - Compact Version */}
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pt: 1.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    minHeight: "60px", // Fixed height for user info
                  }}>
                    <Avatar
                      sx={{
                        backgroundColor: getAvatarColor(review.userId),
                        color: "white",
                        fontWeight: 600,
                        width: 36,
                        height: 36,
                        fontSize: "0.813rem",
                        flexShrink: 0
                      }}
                    >
                      {getInitials(review.firstName, review.lastName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            wordBreak: "keep-all",
                          }}
                        >
                          {review.firstName} {review.lastName}
                        </Typography>
                        {review.verified && (
                          <Verified sx={{
                            color: "#455cefff",
                            fontSize: 14,
                            flexShrink: 0
                          }} />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatDisplayDate(review.date)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* User Media Section - Compact Version */}
                  {renderCompactMedia(review)}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Mobile Navigation Buttons */}
          {isMobile && reviews.length > 0 && (
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2.5,
              px: 2
            }}>
              <IconButton
                onClick={scrollLeft}
                sx={{
                  backgroundColor: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  width: 44,
                  height: 44,
                }}
              >
                <ArrowBackIos sx={{ ml: 0.5 }} />
              </IconButton>
              <IconButton
                onClick={scrollRight}
                sx={{
                  backgroundColor: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  width: 44,
                  height: 44,
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            </Box>
          )}

          {/* Page Navigation */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 3.5,
              flexWrap: 'wrap'
            }}>
              <IconButton
                onClick={loadPreviousPage}
                disabled={currentPage === 0}
                sx={{
                  backgroundColor: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                    color: "#999"
                  },
                  width: 44,
                  height: 44
                }}
              >
                <ArrowBackIos sx={{ fontSize: 20, ml: 0.5 }} />
              </IconButton>

              <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, fontSize: "0.938rem" }}>
                Page {currentPage + 1} of {totalPages}
              </Typography>

              <IconButton
                onClick={loadNextPage}
                disabled={currentPage >= totalPages - 1}
                sx={{
                  backgroundColor: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#eb2329",
                    color: "white",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                    color: "#999"
                  },
                  width: 44,
                  height: 44
                }}
              >
                <ArrowForwardIos sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {/* Media Modal */}
      <Modal
        open={mediaModalOpen}
        onClose={closeMediaModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 }
        }}
      >
        <Fade in={mediaModalOpen}>
          <Box sx={{
            position: 'relative',
            width: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: 900,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
            outline: 'none'
          }}>

            <IconButton
              onClick={closeMediaModal}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                zIndex: 10,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }
              }}
            >
              <Close />
            </IconButton>

            {selectedReviewId && reviewMedia[selectedReviewId] && (
              <Box sx={{ position: 'relative' }}>
                <Box sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '60vh' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#000',
                  position: 'relative'
                }}>
                  {renderModalMedia(
                    reviewMedia[selectedReviewId].media[selectedMediaIndex],
                    selectedReviewId,
                    selectedMediaIndex
                  )}

                  {reviewMedia[selectedReviewId].media.length > 1 && (
                    <>
                      <IconButton
                        onClick={previousMedia}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                          }
                        }}
                      >
                        <ArrowBackIos sx={{ ml: 0.5 }} />
                      </IconButton>
                      <IconButton
                        onClick={nextMedia}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                          }
                        }}
                      >
                        <ArrowForwardIos />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Box sx={{
                  p: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'white'
                }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        backgroundColor: getAvatarColor(reviews.find(r => r.id === selectedReviewId)?.userId || 0),
                        color: 'white',
                        fontWeight: 600,
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 }
                      }}
                    >
                      {reviews.find(r => r.id === selectedReviewId) ?
                        getInitials(
                          reviews.find(r => r.id === selectedReviewId).firstName,
                          reviews.find(r => r.id === selectedReviewId).lastName
                        ) : 'US'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {reviews.find(r => r.id === selectedReviewId)?.firstName} {reviews.find(r => r.id === selectedReviewId)?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        wordBreak: 'break-word'
                      }}>
                        {reviews.find(r => r.id === selectedReviewId)?.carBrandName} {reviews.find(r => r.id === selectedReviewId)?.carModelName}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                      {reviewMedia[selectedReviewId].media[selectedMediaIndex].mediaType === 'VIDEO' ? (
                        <VideoCameraBack sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                      ) : (
                        <Image sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {selectedMediaIndex + 1} of {reviewMedia[selectedReviewId].media.length}
                      </Typography>
                    </Box>
                  </Stack>

                  {reviewMedia[selectedReviewId].media.length > 1 && (
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 2,
                      overflowX: 'auto',
                      py: 1,
                      '&::-webkit-scrollbar': {
                        height: 6
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f5f5f5',
                        borderRadius: 3
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#ddd',
                        borderRadius: 3,
                        '&:hover': {
                          backgroundColor: '#bbb'
                        }
                      }
                    }}>
                      {reviewMedia[selectedReviewId].media.map((media, index) => (
                        <Box
                          key={media.mediaId}
                          onClick={() => setSelectedMediaIndex(index)}
                          sx={{
                            width: { xs: 60, sm: 80 },
                            height: { xs: 45, sm: 60 },
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative',
                            flexShrink: 0,
                            border: selectedMediaIndex === index ? '2px solid #eb2329' : '2px solid transparent',
                            transition: 'border-color 0.2s',
                            '&:hover': {
                              borderColor: '#eb2329'
                            }
                          }}
                        >
                          {media.mediaType === 'VIDEO' ? (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#333',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {media.thumbnailUrl ? (
                                <>
                                  <img
                                    src={media.thumbnailUrl}
                                    alt={`Video thumbnail ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      opacity: 0.7
                                    }}
                                  />
                                  <PlayArrow sx={{
                                    color: 'white',
                                    fontSize: { xs: 16, sm: 20 },
                                    position: 'absolute'
                                  }} />
                                </>
                              ) : (
                                <PlayArrow sx={{
                                  color: 'white',
                                  fontSize: { xs: 16, sm: 20 }
                                }} />
                              )}
                            </Box>
                          ) : media.mediaUrl ? (
                            <img
                              src={media.mediaUrl}
                              alt={`Thumbnail ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Image sx={{ color: '#ccc', fontSize: { xs: 16, sm: 20 } }} />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ReviewSection;